import { useCallback, useEffect, useState } from "react";
import type { AttendeeResource, AttendeeResourceList, PublicResourceList } from "@verity-connect/contracts";

/**
 * A guest gets the list without anything reachable in it, so the app can show
 * what an account opens. A signed-in attendee gets the same list with the
 * addresses and file names filled in.
 */
export interface SessionResource {
  id: string | null;
  itemId: string;
  slot: AttendeeResource["slot"];
  title: string;
  kind: AttendeeResource["kind"];
  url: string | null;
  fileName: string | null;
  mediaType: string | null;
  sizeBytes: number | null;
  convertedFile: AttendeeResource["convertedFile"];
  open: boolean;
  opensAt: string;
}

export type SessionResourcesState =
  | { status: "loading" }
  | { status: "ready"; resources: readonly SessionResource[]; signedIn: boolean }
  | { status: "unavailable" };

export type ReloadableSessionResourcesState = SessionResourcesState & { retry: () => void };

export function useSessionResources(signedIn: boolean, eventSlug: string): ReloadableSessionResourcesState {
  const [state, setState] = useState<SessionResourcesState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setState({ status: "loading" });
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const path = `/api/${signedIn ? "attendee" : "public"}/events/${encodeURIComponent(eventSlug)}/resources`;
    void fetch(path, { signal: controller.signal, credentials: "same-origin" })
      .then(async (response) => {
        if (!response.ok) throw new Error("unavailable");
        const body = await response.json() as AttendeeResourceList | PublicResourceList;
        setState({
          status: "ready",
          signedIn,
          resources: body.resources.map((entry) => ({
            id: "id" in entry ? entry.id : null,
            itemId: entry.itemId,
            slot: entry.slot,
            title: entry.title,
            kind: entry.kind,
            url: "url" in entry ? entry.url : null,
            fileName: "fileName" in entry ? entry.fileName : null,
            mediaType: "mediaType" in entry ? entry.mediaType : null,
            sizeBytes: entry.sizeBytes,
            convertedFile: "convertedFile" in entry ? entry.convertedFile : null,
            open: entry.open,
            opensAt: entry.opensAt,
          })),
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ status: "unavailable" });
      });
    return () => controller.abort();
  }, [attempt, eventSlug, signedIn]);

  return { ...state, retry };
}

export function resourcesForItem(
  state: SessionResourcesState,
  itemId: string,
): readonly SessionResource[] {
  return state.status === "ready" ? state.resources.filter((entry) => entry.itemId === itemId) : [];
}
